import httpClient, { ConexaResponse } from '@/services/httpClient'
import {
  IUser,
  IUserData,
  ClinicAndFlags,
  IUserDataResponse,
  MagicLinkLoginResponse,
  SendTwoFactorAuthCodeResponse,
  SendTwoFactorAuthCode,
  IGeolocation,
  IUserDataInfo,
  IUserDataInfoResponse,
} from '@/modules/Auth/auth-types'
import axios from 'axios'
import { MAGIC_LINK_MULTIPROFILE_TOKEN } from '@/constants'

export default {
  async login(
    user: string,
    pass: string,
    captchaToken: string,
    useRefreshToken = false,
  ): Promise<IUser[]> {
    return httpClient
      .post(
        '/integration/app-paciente/usuario/login/web',
        {
          email: user,
          senha: pass,
          audience: 'WEB',
          utilizaRefreshToken: useRefreshToken,
        },
        {
          headers: {
            captcha: captchaToken,
          },
        },
      )
      .then(res => {
        return res.data.object
      })
  },

  async getClinic(userToken: string): Promise<ClinicAndFlags> {
    return httpClient
      .get('/integration/app-paciente/clinica/configuracoes', {
        headers: {
          token: userToken,
        },
      })
      .then(res => {
        const object = JSON.parse(JSON.stringify(res.data.object))
        object.clinic = {}
        object.flags = {}
        Object.keys(res.data.object).forEach(function (item) {
          if (item !== 'flags' && item !== 'clinic') {
            if (item.includes('flag')) {
              object.flags[item.replace('flag', '')] = res.data.object[item]
              delete object[item]
            } else {
              object.clinic[item] = object[item]
              delete object[item]
            }
          }
        })
        return object
      })
  },

  async getUserIP(): Promise<string> {
    return axios.get('https://www.cloudflare.com/cdn-cgi/trace').then(res => {
      return res.data.split('\n')[2].split('=')[1]
    })
  },

  async getUserData(): Promise<IUserData> {
    const response = await httpClient.get<ConexaResponse<IUserDataResponse>>(
      '/usuario/meusdados',
    )
    const rawUserData = response?.data?.object

    const userData: IUserData = {
      contactsEmergency: {
        relationship: rawUserData?.contatoEmergencia?.relacao,
        fullName: rawUserData?.contatoEmergencia?.nomeCompleto,
        cellphone: rawUserData?.contatoEmergencia?.telefone,
        emergencyContactId: rawUserData?.contatoEmergencia?.idContatoEmergencia,
      },
      user: {
        id: rawUserData?.usuario?.id,
        blocked: rawUserData?.usuario?.flagBloqueado,
        clinic: {
          id: rawUserData?.usuario?.clinica?.id,
        },
        patient: {
          address: {
            cep: rawUserData?.usuario?.paciente?.cep || '',
            formattedCep: rawUserData?.usuario?.paciente?.cepFormatado || '',
            streetName: rawUserData?.usuario?.paciente?.endereco || '',
            number: rawUserData?.usuario?.paciente?.numeroLogradouro || '',
            complement: rawUserData?.usuario?.paciente?.complemento || '',
            neighborhood: rawUserData?.usuario?.paciente?.bairro || '',
            city: rawUserData?.usuario?.paciente?.cidade || '',
            state: rawUserData?.usuario?.paciente?.uf || '',
          },
          birthDate: rawUserData?.usuario?.paciente?.dataNascimento,
          email: rawUserData?.usuario?.paciente?.email,
          name: rawUserData?.usuario?.paciente?.nome,
          phoneNumber: rawUserData?.usuario?.paciente?.telefoneCelular,
          parsedPhoneNumber:
            rawUserData?.usuario?.paciente?.telefoneCelularFormatado,
          photo: rawUserData?.usuario?.paciente?.urlFoto,
          socialName: rawUserData?.usuario?.paciente?.nomeSocial || '',
          passport: rawUserData?.usuario?.paciente?.passaporte,
          dateIssuePassport:
            rawUserData?.usuario?.paciente?.dataEmissaoPassaporte,
          passportExpirationDate:
            rawUserData?.usuario?.paciente?.dataValidadePassaporte,
          foreignBirthCountryId:
            rawUserData?.usuario?.paciente?.idPaisNascimentoEstrangeiro,
          birthState: rawUserData?.usuario?.paciente?.ufNascimento,
          issuingBodyRg: rawUserData?.usuario?.paciente?.orgaoEmissorIdentidade,
          dateIssueRg: rawUserData?.usuario?.paciente?.dataEmissaoIdentidade,
          rg: rawUserData?.usuario?.paciente?.identidade,
          cpf: rawUserData?.usuario?.paciente?.cpf,
          ethnicity: rawUserData?.usuario?.paciente?.etnia,
          idGenre: rawUserData?.usuario?.paciente?.idGenero,
          idNacionality: rawUserData?.usuario?.paciente?.idNacionalidade,
          idRaceOrColor: rawUserData?.usuario?.paciente?.idRacaCor,
          nameMother: rawUserData?.usuario?.paciente?.nomeMae,
          unknownMother: !!rawUserData?.usuario?.paciente?.flagMaeDesconhecida,
          cns: rawUserData?.usuario?.paciente?.cns,
          sex: rawUserData?.usuario?.paciente?.sexo,
          birthCity: rawUserData?.usuario?.paciente?.municipioNascimento,
          idUfBirth: rawUserData?.usuario?.paciente?.idUfNascimento,
          healthInsuranceNumber:
            rawUserData?.usuario?.paciente?.carteiraConvenio,
          needToUpdateAccountData:
            rawUserData?.usuario?.paciente?.pendenteAtualizacaoDadosCadastrais,
        },
      },
    }

    return userData || null
  },

  async getUserDataInfo(): Promise<IUserDataInfo> {
    const response = await httpClient.get<
      ConexaResponse<IUserDataInfoResponse>
    >('/integration/app-paciente/dados-pessoais/obter-dados-basicos')
    const rawUserDataInfo = response?.data?.object
    const userDataInfo: IUserDataInfo = {
      name: rawUserDataInfo?.nome,
      birthDate: rawUserDataInfo?.dataNascimento,
      cpf: rawUserDataInfo?.cpf,
      phoneNumber: rawUserDataInfo?.celular,
      email: rawUserDataInfo?.email,
      sex: rawUserDataInfo?.sexo,
      streetName: rawUserDataInfo?.endereco || '',
      complement: rawUserDataInfo?.complemento || '',
      cep: rawUserDataInfo?.cep || '',
      formattedCep: rawUserDataInfo?.cepFormatado || '',
      city: rawUserDataInfo?.cidade || '',
      neighborhood: rawUserDataInfo?.bairro || '',
      number: rawUserDataInfo?.numeroLogradouro || '',
      state: rawUserDataInfo?.uf || '',
      contactsEmergency: {
        relationship: rawUserDataInfo?.contatoEmergencia?.relacao,
        fullName: rawUserDataInfo?.contatoEmergencia?.nomeCompleto,
        cellphone: rawUserDataInfo?.contatoEmergencia?.telefone,
        emergencyContactId:
          rawUserDataInfo?.contatoEmergencia?.idContatoEmergencia,
      },
    }
    return userDataInfo || null
  },
  async magicLinkLogin(token: string): Promise<IUser[]> {
    const response = await httpClient.post<MagicLinkLoginResponse>(
      `/integration/app-paciente/usuario/login/link-magico-paciente/`,
      {
        tokenId: token,
        audience: 'WEB',
      },
    )

    return response?.data?.object || []
  },

  async magicLinkLoginMultiprofile(redirectToken: string): Promise<IUser[]> {
    const token = MAGIC_LINK_MULTIPROFILE_TOKEN
    const response = await httpClient.get<MagicLinkLoginResponse>(
      `/cnx/paciente/magic-link/${redirectToken}/v1/`,
      {
        headers: {
          token,
        },
      },
    )

    return response?.data?.object || []
  },

  async acceptTerm(): Promise<void> {
    await httpClient.get('/integration/app-paciente/usuario/aceitar-termo')
  },

  validateToken: async (token: string): Promise<void> => {
    await httpClient.get('/integration/app-paciente/usuario/validar-token', {
      headers: {
        token,
      },
    })
  },

  authMagicLink: async (token: string): Promise<string | null> => {
    const response = await httpClient.get(
      `/integration/app-paciente/usuario/autenticacao-tokenid/${token}`,
    )

    return response?.data?.object || null
  },

  checkTermsAcceptance: async (): Promise<boolean> => {
    const response = await httpClient.get(
      '/integration/app-paciente/usuario/termo-aceito',
    )

    return response.data.object
  },

  sendChangePasswordEmail: async (
    email: string,
    captchaToken: string,
  ): Promise<boolean> => {
    const response = await httpClient.post(
      '/usuario/esqueciSenha/paciente',
      {
        email,
      },
      {
        headers: {
          captcha: captchaToken,
        },
      },
    )

    return response?.data?.object || false
  },

  verifyPassword: async (
    password: string,
    captchaToken: string,
  ): Promise<boolean> => {
    const response = await httpClient.post(
      '/integration/app-paciente/usuario/verificarsenha',
      {
        senha: password,
      },
      {
        headers: {
          captcha: captchaToken,
        },
      },
    )

    return response?.data?.object
  },

  changePassword: async (
    password: string,
    confirmPassword: string,
  ): Promise<boolean> => {
    const response = await httpClient.post(
      '/integration/app-paciente/usuario/modificar-senha',
      {
        senha: password,
        csenha: confirmPassword,
      },
    )

    return response?.data?.object || false
  },

  validateFirstAccess: async (token: string): Promise<boolean> => {
    const response = await httpClient.post(
      'integration/app-paciente/usuario/atualizar-primeiro-acesso',
      {},
      {
        headers: {
          token,
        },
      },
    )
    return response?.data?.object
  },

  sendGeolocation: async (data: IGeolocation): Promise<boolean> => {
    const response = await httpClient.post(
      'integration/app-paciente/geolocalizacao/salvar',
      data,
    )
    return response?.data?.object
  },

  sendTwoFactorAuthCode: async (): Promise<SendTwoFactorAuthCode> => {
    const response = await httpClient.get<
      ConexaResponse<SendTwoFactorAuthCodeResponse>
    >('/integration/app-paciente/autenticacao2fa/enviar-codigo')

    return {
      codeSentAt: response?.data?.object?.momentoCriacao,
      attemptsCount: response?.data?.object?.qtdTentativas,
    }
  },

  validateTwoFactorAuthCode: async (code: string): Promise<boolean> => {
    const response = await httpClient.get<ConexaResponse<boolean>>(
      `/integration/app-paciente/autenticacao2fa/verificar-codigo/${code}`,
    )

    return response?.data?.object
  },
}
